import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Divider,
  Flex,
  Form,
  FormButton,
  FormDateInput,
  Text,
} from '@shared/components';
import { FormProvider, useForm } from 'react-hook-form';

import { useExamResultFileListSearchParams } from '../../hooks';

import {
  type ExamResultFileFilterFormInput,
  type ExamResultFileFilterFormOutput,
  ExamResultFileFilterFormSchema,
} from './exam-result-file-filter-form-schema';

export default function ExamResultFileFilterForm() {
  const [{ createdDateFrom, createdDateTo }, { changeCreatedDateRange }] =
    useExamResultFileListSearchParams();

  const formContext = useForm<
    ExamResultFileFilterFormInput,
    unknown,
    ExamResultFileFilterFormOutput
  >({
    resolver: zodResolver(ExamResultFileFilterFormSchema),
    defaultValues: {
      createdDateFrom: createdDateFrom ?? null,
      createdDateTo: createdDateTo ?? null,
    },
  });

  const onSubmit = (data: ExamResultFileFilterFormInput) => {
    changeCreatedDateRange(data.createdDateFrom, data.createdDateTo);
  };

  const handleResetFilter = () => {
    formContext.reset({ createdDateFrom: null, createdDateTo: null });
    changeCreatedDateRange(null, null);
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(onSubmit)}>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(4),
            p: theme.spacing(4),
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: theme.shape.rounded(2),
          })}
        >
          <Flex sx={(theme) => ({ gap: theme.spacing(3) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '160px' }}>
              Date Filter:
            </Text>
            <Flex
              sx={(theme) => ({
                gap: theme.spacing(4),
                flex: 1,
                alignItems: 'flex-start',
              })}
            >
              <Box sx={{ flexBasis: '256px' }}>
                <FormDateInput
                  label='Start Date'
                  name='createdDateFrom'
                  inputFormat='DD MMM BBBB'
                  labelFormat='MMMM BBBB'
                  placeholder='DD-MM-YYYY'
                  yearLabelFormat='BBBB'
                  fullWidth
                />
              </Box>
              <Text sx={(theme) => ({ whiteSpace: 'nowrap', pt: theme.spacing(8) })}>to</Text>
              <Box sx={{ flexBasis: '256px' }}>
                <FormDateInput
                  label='End Date'
                  name='createdDateTo'
                  inputFormat='DD MMM BBBB'
                  labelFormat='MMMM BBBB'
                  placeholder='DD-MM-YYYY'
                  yearLabelFormat='BBBB'
                  fullWidth
                />
              </Box>
            </Flex>
          </Flex>
          <Divider />
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(2),
              justifyContent: 'flex-end',
            })}
          >
            <Button color='secondary' variant='outlined' type='reset' onClick={handleResetFilter}>
              Reset
            </Button>
            <FormButton label='Apply' variant='contained' />
          </Flex>
        </Flex>
      </Form>
    </FormProvider>
  );
}
